'use client';

import { useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, CircularProgress, InputBase, Typography } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslator, useAppLanguage } from '@/lang';
import { useDarkColors } from '@/hooks/useDarkColors';
import { useAuthStore } from '@/stores/authStore';
import { useToastStore } from '@/stores/toastStore';
import { getWithdrawReasons } from '../../api/inquiryApi';
import {
  TEMP_TEXT_BOLD_XL,
  TEMP_TEXT_BOLD_XXL,
  TEMP_TEXT_MEDIUM_M,
  TEMP_TEXT_MEDIUM_XL,
  TEMP_TEXT_REGULAR_XS,
  TEMP_TEXT_SEMIBOLD_XL,
} from '@/constants/tempTypo';
import { TEMP_FG_INVERSE } from '@/constants/tempColors';

const ETC_KEY = 'ETC';
const MAX_CUSTOM_REASON = 500;

interface WithdrawSheetProps {
  onClose: () => void;
  onComplete: () => void;
}

/**
 * 회원탈퇴 바텀시트
 * - 사유 선택(라디오) + ETC 직접 입력
 * - 백엔드 /auth/withdraw 호출 후 토큰 폐기 → onComplete 콜백
 */
export default function WithdrawSheet({ onClose, onComplete }: WithdrawSheetProps) {
  const { t } = useTranslator();
  const { language } = useAppLanguage();
  const c = useDarkColors();
  const showToast = useToastStore((s) => s.show);
  const withdraw = useAuthStore((s) => s.withdraw);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [customReason, setCustomReason] = useState<string>('');
  const customInputRef = useRef<HTMLDivElement>(null);

  const toggleReason = (key: string) => {
    setSelectedKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];

      // ETC 를 새로 켤 때만 한 번, 인풋이 화면 밖이면 보이는 영역까지 끌어올림.
      // (포커스/키보드 이벤트는 트리거하지 않음 — iOS visualViewport 흔들림 회피)
      if (key === ETC_KEY && !prev.includes(ETC_KEY)) {
        setTimeout(() => {
          customInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 80);
      }

      return next;
    });
  };

  const { data: reasonsData, isLoading } = useQuery({
    queryKey: ['withdrawReasons'],
    queryFn: getWithdrawReasons,
    staleTime: 1000 * 60 * 60,
  });

  const reasons = useMemo(() => reasonsData?.reasons ?? [], [reasonsData]);

  const getLabel = (label: { ko: string; en: string; ja: string }) => {
    return (label as Record<string, string>)[language] ?? label.ko;
  };

  const isEtc = selectedKeys.includes(ETC_KEY);
  const trimmedCustom = customReason.trim();
  const isFormValid = selectedKeys.length > 0 && (!isEtc || trimmedCustom.length > 0);

  const submitMutation = useMutation({
    mutationFn: () =>
      withdraw({
        reasonKeys: selectedKeys,
        customReason: isEtc ? trimmedCustom : undefined,
      }),
    onSuccess: () => {
      onClose();
      showToast(
        t('withdraw.toast_success_title'),
        t('withdraw.toast_success_message'),
      );
      onComplete();
    },
    onError: () => {
      showToast(
        t('withdraw.toast_error_title'),
        t('withdraw.toast_error_message'),
      );
    },
  });

  const handleSubmit = () => {
    if (!isFormValid || submitMutation.isPending) return;
    submitMutation.mutate();
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: c.bg,
      }}
    >
      {/* 스크롤 영역 — 버튼까지 같이 스크롤됨. data-scroll-container 로 글로벌 touchmove 차단에서 화이트리스트 처리 */}
      <Box
        data-scroll-container
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          px: '20px',
          pt: '8px',
          pb: '32px',
        }}
      >
      {/* 헤더 — 우는 이모지 + 빌고있는 일러스트 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', pt: '8px' }}>
        <Box
          sx={{
            position: 'relative',
            width: 120,
            height: 120,
          }}
        >
          {/* 원형 이미지 */}
          <Box
            component="img"
            src="/images/withdraw_pleading.png"
            alt={t('withdraw.pleading_alt')}
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              objectFit: 'cover',
              border: `3px solid ${c.primaryLow}`,
              boxShadow: '0 8px 24px rgba(124, 58, 237, 0.25)',
            }}
          />
          {/* 우측 하단 ㅠㅠ 이모지 배지 */}
          <Box
            sx={{
              position: 'absolute',
              right: -4,
              bottom: -4,
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: c.bg,
              border: `2px solid ${c.primaryLow}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              lineHeight: 1,
            }}
          >
            😭
          </Box>
        </Box>
        <Typography
          sx={{
            ...TEMP_TEXT_BOLD_XXL,
            color: c.fg,
            textAlign: 'center',
          }}
        >
          {t('withdraw.title')}
        </Typography>
        <Typography
          sx={{
            ...TEMP_TEXT_MEDIUM_M,
            color: c.fgLow,
            textAlign: 'center',
            whiteSpace: 'pre-line',
          }}
        >
          {t('withdraw.subtitle')}
        </Typography>
      </Box>

      {/* 경고 박스 */}
      <Box
        sx={{
          bgcolor: c.bgNotice,
          borderRadius: '12px',
          px: '14px',
          py: '12px',
        }}
      >
        <Typography
          sx={{
            ...TEMP_TEXT_REGULAR_XS,
            color: c.primary,
            whiteSpace: 'pre-line',
            textAlign: 'center',
          }}
        >
          {t('withdraw.warning')}
        </Typography>
      </Box>

      {/* 사유 라벨 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', px: '4px' }}>
        <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow }}>
          {t('withdraw.reason_label')}
        </Typography>
        <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fgLower }}>
          {t('withdraw.reason_hint_multi')}
        </Typography>
      </Box>

      {/* 사유 리스트 — 다중 선택(체크박스) */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: '40px' }}>
          <CircularProgress size={24} sx={{ color: c.fgLower }} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {reasons.map((reason) => {
            const isSelected = selectedKeys.includes(reason.key);
            return (
              <ButtonBase
                key={reason.key}
                onClick={() => toggleReason(reason.key)}
                sx={{
                  borderRadius: '14px',
                  border: `1.5px solid ${isSelected ? c.primary : c.border}`,
                  bgcolor: isSelected ? c.bgNotice : c.bg,
                  px: '16px',
                  py: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'all 0.15s ease',
                  justifyContent: 'flex-start',
                }}
              >
                <Typography sx={{ fontSize: 22, lineHeight: 1 }}>{reason.emoji}</Typography>
                <Typography
                  sx={{
                    ...TEMP_TEXT_MEDIUM_XL,
                    color: isSelected ? c.primary : c.fg,
                    textAlign: 'left',
                    flex: 1,
                  }}
                >
                  {getLabel(reason.label)}
                </Typography>
                {/* 체크박스 */}
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '6px',
                    border: `2px solid ${isSelected ? c.primary : c.fgLower}`,
                    bgcolor: isSelected ? c.primary : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.15s ease',
                  }}
                >
                  {isSelected && (
                    <Box
                      component="svg"
                      viewBox="0 0 24 24"
                      sx={{ width: 14, height: 14 }}
                    >
                      <path
                        d="M5 12l5 5L20 7"
                        fill="none"
                        stroke="#ffffff"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </Box>
                  )}
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      )}

      {/* ETC 직접 입력 */}
      {isEtc && (
        <Box ref={customInputRef} sx={{ display: 'flex', flexDirection: 'column', gap: '8px', scrollMarginTop: '8px' }}>
          <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow, px: '4px' }}>
            {t('withdraw.custom_label')}
          </Typography>
          <Box
            sx={{
              border: `1px solid ${c.border}`,
              borderRadius: '14px',
              px: '16px',
              py: '12px',
              minHeight: 100,
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <InputBase
              autoFocus
              value={customReason}
              onChange={(e) => {
                if (e.target.value.length <= MAX_CUSTOM_REASON) {
                  setCustomReason(e.target.value);
                }
              }}
              placeholder={t('withdraw.custom_placeholder')}
              multiline
              minRows={3}
              fullWidth
              sx={{
                ...TEMP_TEXT_MEDIUM_XL,
                color: c.fg,
                flex: 1,
                alignItems: 'flex-start',
                '& textarea::placeholder': {
                  color: c.fgLower,
                  opacity: 1,
                },
              }}
            />
            <Typography
              sx={{
                ...TEMP_TEXT_REGULAR_XS,
                color: c.fgLow,
                textAlign: 'right',
              }}
            >
              {customReason.length} / {MAX_CUSTOM_REASON}
            </Typography>
          </Box>
        </Box>
      )}

        {/* 버튼 — 스크롤 영역 안쪽 (고정 X, 함께 스크롤됨) */}
        <Box sx={{ display: 'flex', gap: '10px', pt: '8px' }}>
          <ButtonBase
            onClick={onClose}
            disabled={submitMutation.isPending}
            sx={{
              flex: 1,
              bgcolor: c.bgInset,
              borderRadius: '14px',
              py: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: c.fg }}>
              {t('withdraw.cancel')}
            </Typography>
          </ButtonBase>
          <ButtonBase
            onClick={handleSubmit}
            disabled={!isFormValid || submitMutation.isPending}
            sx={{
              flex: 1.2,
              bgcolor: isFormValid ? '#E53935' : c.fgDisabled,
              borderRadius: '14px',
              py: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent',
              transition: 'background-color 0.15s ease',
            }}
          >
            {submitMutation.isPending ? (
              <CircularProgress size={22} sx={{ color: TEMP_FG_INVERSE }} />
            ) : (
              <Typography sx={{ ...TEMP_TEXT_BOLD_XL, color: TEMP_FG_INVERSE }}>
                {t('withdraw.confirm')}
              </Typography>
            )}
          </ButtonBase>
        </Box>
      </Box>
    </Box>
  );
}
